import React, {
	useState,
	useEffect,
	useMemo,
	useRef,
	useCallback
} from "react";
import classnames from "classnames";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
	getNextSearchResultsPage,
	getSearchPage,
	clearRedirectTo,
	pushSearchFromMapMoveByCoordinates,
	applySearchClient
} from "store/modules/search";
import { setGeolocation } from "store/modules/user";
import { searchPageSelector } from "store/selectors/SearchPage";

import { Section, Container, Page } from "components/core/Page";
import { Paragraph, Text } from "components/core/Typography";
import { ScrollToTop } from "components/core/ScrollToTop";
import { Breadcrumbs } from "components/search/Breadcrumbs";
import { SearchBar } from "components/search/SearchBar";
import SisterPages from "components/search/SearchLandingContent/SisterPages";
import NearbyCities from "components/search/SearchLandingContent/NearbyCities";

import { FILTER_GROUPS } from "config/filters";
import { getNMPBreadcrumbLabel, getKeywordConfig } from "config/nearMeKeywords";
import useSearchFilters from "hooks/search/useSearchFilters";
import useActiveFilterCount from "hooks/search/useActiveFilterCount";
import {
	decodeQueryStringValue,
	buildSearchFilterLink
} from "utils/search/buildSearchLink";
import { getDisplayCityName } from "utils/location";
import { getSearchPageSyncTargetPath } from "utils/search/searchPageUrlSync";
import { getSearchMeta } from "utils/search/getSearchMeta";
import Row from "components/core/Page/Row";
import Column from "components/core/Page/Column";
import SearchResults from "components/organisms/search/SearchResults";
import { Button } from "components/core/Button";
import { LoadingBoxes } from "components/core/Icons";
import { HTMLContent } from "components/core/HTMLContent";
import DealsFilter from "components/organisms/DealsFilter";

import {
	generateCLPWebPageSchema,
	generateCLPWebsiteSchema,
	organizationSchema
} from "config/schemas";
import createSelfStorageSchema from "@sparefoot/react-commons/utils/createSelfStorageSchema";
import { mapAmenitiesToFeature } from "config/amenityMap";

import usePageViewTracking from "hooks/tracking/usePageViewTracking";
import { PAGE_TYPES, PAGE_SUB_TYPES } from "@sparefoot/tracking-events";

import SearchPageContext from "pages/SearchPageV1/SearchPageV1Context";
import ClickToCallTray from "components/organisms/search/ClickToCallTray/ClickToCallTray";
import StickToolBar from "components/organisms/search/StickToolBar/StickToolBar";

import { Helmet } from "react-helmet-async";
import { resolveMapCenter } from "components/google-maps";

import { combineSchemas } from "./utils/combineSchemas";
import { buildNmpSchema } from "./utils/buildNmpSchema";

import "./SearchPageV2.scss";

import useMapSearch from "./useMapSearch";
import SearchPageV2MapColumn from "./SearchPageV2MapColumn";

export default function SearchPageV2() {
	const {
		cityUrl,
		meta: baseMeta,
		pageText = "",
		pageType,
		subPageType,
		location,
		stateUrl,
		currentPage,
		hasMorePages,
		totalListings,
		nearbyCities,
		amenityPages,
		facilityCards,
		isCallCenterOpen: isCallCenterOpenFromState,
		dealsFilter
	} = useSelector(searchPageSelector, shallowEqual);
	// isCallCenterOpenFromState is null on the server and the live value on
	// the client's very first render, which mismatches SSR markup and fails
	// hydration. Start at the SSR-safe default and only adopt the live value
	// after mount, once hydration has already reconciled successfully.
	const [isCallCenterOpen, setIsCallCenterOpen] = useState(false);
	useEffect(() => {
		setIsCallCenterOpen(isCallCenterOpenFromState);
	}, [isCallCenterOpenFromState]);
	const isSearchLoading = useSelector(
		(reduxState) => reduxState?.search?.loading
	);
	const baseUrl = useSelector((reduxState) => reduxState?.app?.baseUrl);
	const redirectTo = useSelector(
		(reduxState) => reduxState.search.redirectTo
	);
	const geolocation = useSelector(
		(reduxState) => reduxState?.user?.geolocation
	);
	const dispatch = useDispatch();
	// Guard when location is empty (e.g. invalid API response or initial load) to avoid undefined/map crash
	const state = location?.state ?? "";
	const preferredPlaceName = location?.preferredPlaceName ?? "";
	const city = location?.city ?? "";
	const { keyword } = useParams();
	const { search, pathname } = useLocation();
	const navigate = useNavigate();
	// Mark the page noindex,nofollow when the URL carries page-state params
	// (filters, pagination, sort, etc.) so permutations don't get indexed.
	// Pure marketing/tracking params (utm_*, gclid, ...) are ignored so paid
	// landing URLs stay indexable.
	const meta = getSearchMeta(baseMeta, search);
	// Memoize so context and hooks get a stable reference when search string is unchanged
	const queryParams = useMemo(() => decodeQueryStringValue(search), [search]);
	const persistedQuery = useSelector(
		(reduxState) => reduxState?.search?.query || null
	);

	const trackingPageType = {
		searchPage: PAGE_TYPES.SEARCH,
		cityLandingPage: PAGE_TYPES.CITY_LANDING,
		amenityLandingPage: PAGE_SUB_TYPES.AMENITY_LANDING,
		nearMeSearchPage: PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING
	};
	usePageViewTracking(trackingPageType[pageType] || PAGE_TYPES.SEARCH);

	const [showFilterModal, setShowFilterModal] = useState(false);
	const [showInlineFilters, setShowInlineFilters] = useState(false);
	const [isFiltering, setIsFiltering] = useState(false);
	const [isLoadingResults, setIsLoadingResults] = useState(false);
	const [showMapView, setShowMapView] = useState(false);
	const filtersContainerRef = useRef(null);
	const mapAndListRef = useRef(null);

	const {
		handleMapMove,
		handleMapLocationClick,
		showMapLoader,
		mapMoveMessage,
		searchFromMove,
		setSearchFromMove,
		selectedFacilityId,
		setSelectedFacilityId,
		hoveredFacilityId,
		setHoveredFacilityId,
		mapRef,
		googleMapsRef,
		pinSelected,
		setPinSelected,
		isOverlayClosing,
		setIsOverlayClosing
	} = useMapSearch();

	const handleShowMapView = () => {
		setShowMapView((v) => !v);
		setPinSelected(false);
		setSelectedFacilityId(null);
		setIsOverlayClosing(false);
	};

	useEffect(() => {
		setIsLoadingResults(false);
	}, [currentPage]);

	// Scroll to top whenever a new search completes (page 1). Does not run on "load more" (page 2+).
	const prevSearchLoadingRef = useRef(isSearchLoading);
	useEffect(() => {
		const wasLoading = prevSearchLoadingRef.current;
		prevSearchLoadingRef.current = isSearchLoading;
		if (wasLoading && !isSearchLoading && currentPage === 1) {
			if (typeof globalThis.window !== "undefined") {
				globalThis.scrollTo({ top: 0, behavior: "smooth" });
			}
		}
	}, [isSearchLoading, currentPage]);

	// React Router already listens to browser Back/Forward and updates
	// location.search. Use that as the single trigger to refetch search
	// results so URL state and Redux state stay in sync.
	const lastSearchRef = useRef(search);
	useEffect(() => {
		// Skip on initial mount; only react to actual changes.
		if (lastSearchRef.current === search) {
			return;
		}
		lastSearchRef.current = search;

		const nextQueryParams = decodeQueryStringValue(search) || {};
		const hasParams = Object.keys(nextQueryParams).length > 0;
		const hasPersisted =
			persistedQuery && Object.keys(persistedQuery).length > 0;

		// If we've navigated back to a URL with no query params but we
		// have a persisted server query (initial landing search), refetch
		// using that persisted query so the UI stays consistent and still
		// shows the loading state. Strip filter/sort params so we don't
		// re-apply stale filters from the previous search.
		if (!hasParams && hasPersisted) {
			// The vehicle groups reach the gateway under different param names
			// than their group keys, so strip those explicitly too.
			const filterKeys = [
				...Object.values(FILTER_GROUPS),
				"vehicleAccommodation",
				"vehicle",
				"minLength",
				"maxLength",
				"onlyLocker",
				"type",
				"searchType",
				"sort"
			];
			const baseQuery = Object.fromEntries(
				Object.entries(persistedQuery).filter(
					([k]) => !filterKeys.includes(k)
				)
			);
			setIsFiltering(true);
			dispatch(
				getSearchPage({
					query: baseQuery
				})
			);
			return;
		}

		// If we have neither URL params nor a persisted query, there's
		// nothing meaningful to refetch.
		if (!hasParams && !hasPersisted) {
			setIsFiltering(false);
			return;
		}

		// Normal case: URL has params, refetch based on URL.
		setIsFiltering(true);
		dispatch(
			getSearchPage({
				query: nextQueryParams
			})
		);
	}, [search, dispatch, persistedQuery]);

	useEffect(() => {
		// When the search request completes (filters or sort applied),
		// clear the filtering state so loaders/animations stop and
		// scroll the results container back to the top.
		// isFiltering is intentionally excluded from deps: the effect should only
		// fire on isSearchLoading transitions, not when isFiltering is first set to
		// true — otherwise it clears immediately before the API call starts.
		if (!isSearchLoading && isFiltering) {
			setIsFiltering(false);

			if (typeof globalThis.window !== "undefined") {
				const mainEl = document.querySelector(
					".search-page .full-page-scrollable .main"
				);
				if (mainEl) {
					mainEl.scrollTop = 0;
				} else {
					globalThis.scrollTo({ top: 0, behavior: "smooth" });
				}
			}
		}
	}, [isSearchLoading]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				filtersContainerRef.current &&
				!filtersContainerRef.current.contains(event.target)
			) {
				setShowInlineFilters(false);
			}
		};

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				setShowInlineFilters(false);
			}
		};

		if (showInlineFilters) {
			document.addEventListener("mousedown", handleClickOutside);
			document.addEventListener("keydown", handleKeyDown);
		}

		return () => {
			if (showInlineFilters) {
				document.removeEventListener("mousedown", handleClickOutside);
				document.removeEventListener("keydown", handleKeyDown);
			}
		};
	}, [showInlineFilters]);

	// Deselect facility when user clicks outside map and list
	useEffect(() => {
		const handleClickOutsideMapAndList = (event) => {
			if (!selectedFacilityId) return;
			if (
				mapAndListRef.current &&
				!mapAndListRef.current.contains(event.target)
			) {
				setPinSelected(false);
				setSelectedFacilityId(null);
				setIsOverlayClosing(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutsideMapAndList);
		return () =>
			document.removeEventListener(
				"mousedown",
				handleClickOutsideMapAndList
			);
	}, [
		selectedFacilityId,
		setPinSelected,
		setSelectedFacilityId,
		setIsOverlayClosing
	]);

	useEffect(() => {
		if (redirectTo) {
			navigate(redirectTo);
			dispatch(clearRedirectTo());
		}
	}, [redirectTo, dispatch]);

	// Sync URL path to API canonical/city when they differ; preserve amenity segments.
	useEffect(() => {
		const targetPath = getSearchPageSyncTargetPath({
			pathname,
			cityUrl,
			canonical: meta?.canonical
		});
		if (!targetPath) return;
		const hash =
			typeof globalThis.window === "undefined"
				? "#searchPage"
				: globalThis.location.hash || "#searchPage";
		navigate(`${targetPath}${search || ""}${hash}`, { replace: true });
	}, [cityUrl, pathname, search, navigate, meta?.canonical]);

	const {
		handleFilterChange,
		searchFilters,
		applySearch,
		selectedSort,
		handleSortChange
	} = useSearchFilters(
		[
			FILTER_GROUPS.AMENITIES,
			FILTER_GROUPS.UNIT_SIZE,
			FILTER_GROUPS.VEHICLE_STORAGE,
			FILTER_GROUPS.VEHICLE_SIZE
		],
		queryParams,
		{ useClientSideSearch: true }
	);

	const activeFilterCount = useActiveFilterCount(searchFilters);

	const isNMP = pageType === "nearMeSearchPage";
	const isDealsPage =
		pageType === "nearMeSearchPage" && subPageType === "deals";

	const searchPageContext = useMemo(
		() => ({
			showFilterModal,
			setShowFilterModal,
			setIsFiltering,
			isFiltering,
			location,
			queryParams,
			searchFilters,
			handleFilterChange,
			applySearch,
			selectedSort,
			handleSortChange,
			totalListings,
			pageType,
			isCallCenterOpen,
			showInlineFilters,
			setShowInlineFilters,
			filtersContainerRef
		}),
		[
			showFilterModal,
			setShowFilterModal,
			setIsFiltering,
			isFiltering,
			location,
			queryParams,
			searchFilters,
			handleFilterChange,
			applySearch,
			selectedSort,
			handleSortChange,
			totalListings,
			pageType,
			isCallCenterOpen,
			showInlineFilters,
			setShowInlineFilters,
			filtersContainerRef
		]
	);

	const loadMore = useCallback(() => {
		setIsLoadingResults(true);
		// NMP's page-1 search resolves location from browser geo-coordinates/IP,
		// not a city name (see SearchClient.getSearch). Overriding location with
		// a city-name string here would re-geocode to the city centroid instead,
		// shifting the distance-ranked result set between requests and causing
		// duplicate/missing facilities on "Load More" (SF-21577). CLP/ALP already
		// search by city name from page 1, so the override is safe for them.
		const params =
			pageType !== "searchPage" && pageType !== "nearMeSearchPage"
				? {
						...queryParams,
						location: `${preferredPlaceName}, ${state}`,
						city: preferredPlaceName,
						state
					}
				: queryParams;
		dispatch(getNextSearchResultsPage({ ...params, page: currentPage }));
	}, [
		dispatch,
		pageType,
		queryParams,
		preferredPlaceName,
		state,
		currentPage
	]);

	const showSearchLandingContent =
		(nearbyCities && nearbyCities.length > 0) || amenityPages;

	let newHeading = "Find Storage Units";

	if (pathname?.includes("climate-control-storage-units")) {
		newHeading = "Find Climate Controlled Storage Units";
	}
	if (pathname?.includes("twenty-four-hour-access-storage-units")) {
		newHeading = "Find 24 Hour Storage Units";
	}
	if (pathname?.includes("drive-up-storage-units")) {
		newHeading = "Find Drive Up Storage Units";
	}

	const nmpBreadcrumbLabel = isNMP
		? getNMPBreadcrumbLabel(subPageType)
		: null;

	const isCLP =
		pageType === "cityLandingPage" || pageType === "amenityLandingPage";
	const headingLocation = `${city}, ${state}`;
	const headingTitle = isNMP
		? nmpBreadcrumbLabel
		: `${newHeading} in ${headingLocation}`;
	const hasCoords = !!(queryParams?.latitude && queryParams?.longitude);

	const onLocate = useCallback(
		({ latitude, longitude }) => {
			dispatch(
				setGeolocation({
					geolocation: {
						city: "",
						region: "",
						country: "US",
						latitude: String(latitude),
						longitude: String(longitude)
					},
					source: "browser"
				})
			);
			if (isNMP) {
				const {
					location: _loc,
					city: _city,
					state: _state,
					...baseParams
				} = persistedQuery || {};
				const params = { ...baseParams, latitude, longitude, page: 1 };
				navigate(
					`${buildSearchFilterLink(pathname, params)}#searchPage`
				);
			} else {
				dispatch(
					pushSearchFromMapMoveByCoordinates({
						pathname,
						queryParams,
						latitude,
						longitude,
						pushUrl: navigate
					})
				);
			}
		},
		[dispatch, isNMP, persistedQuery, pathname, queryParams, navigate]
	);

	const stickToolBarProps = {
		headingTitle,
		filterCount: activeFilterCount,
		location: headingLocation,
		isNMP,
		city,
		hasCoords,
		onLocate
	};
	const currentFacilityCount = facilityCards.length;

	const mapCenter = useMemo(
		() =>
			resolveMapCenter({
				searchLocation: location,
				facilityCards,
				geolocation
			}),
		[location, facilityCards, geolocation]
	);

	// TODO : reuse this schema for other pages and move to a more global utils file
	const breadcrumbItems = [
		{
			label: "Home",
			href: "/",
			name: "Home"
		}
	];

	if (isNMP && nmpBreadcrumbLabel) {
		breadcrumbItems.push({
			label: nmpBreadcrumbLabel,
			name: nmpBreadcrumbLabel,
			href: pathname
		});
	} else {
		if (stateUrl && location?.fullState) {
			breadcrumbItems.push({
				label: location.fullState,
				name: location.fullState,
				href: stateUrl
			});
		}

		if (cityUrl && preferredPlaceName) {
			breadcrumbItems.push({
				label: preferredPlaceName,
				name: preferredPlaceName,
				href: cityUrl
			});
		}
	}

	const breadcrumbListSchema = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"@id": `${meta?.canonical}#breadcrumb`,
		itemListElement: breadcrumbItems
			.filter((crumb) => crumb.label)
			.map((crumb, index) => ({
				"@type": "ListItem",
				position: index + 1,
				item: {
					"@id": `${baseUrl}${crumb.href}`,
					name: crumb.name || crumb.label,
					url: `${baseUrl}${crumb.href}`
				}
			}))
	};

	const clpWebPageSchema = generateCLPWebPageSchema({
		city,
		state,
		canonicalUrl: meta?.canonical,
		title: meta?.title,
		description: meta?.description
	});
	const clpWebSiteSchema = generateCLPWebsiteSchema({
		baseUrl
	});

	const facilitySchemas = facilityCards.map((facility) => {
		const schema = facility.schema || {};
		const amenityFeature = mapAmenitiesToFeature({
			amenities: schema?.amenities || {}
		});

		const paymentAccepted = schema?.paymentAccepted;
		const openingHours =
			schema?.openingHours || schema?.openingHoursSpecification;
		const sameAs = Array.isArray(schema?.sameAs)
			? schema.sameAs
			: undefined;

		const baseStorageSchema = createSelfStorageSchema({
			...schema,
			amenityFeature,
			staticMapUrl: null,
			paymentAccepted,
			openingHours,
			sameAs
		});

		const { latitude, longitude } = schema?.location || {};
		return {
			...baseStorageSchema,
			"@id": `${schema?.url}#selfstorage`,
			priceRange: "$ - $$",
			hasMap:
				latitude && longitude
					? `https://www.google.com/maps?q=${latitude},${longitude}`
					: undefined
		};
	});

	const nmpKeywordConfig =
		isNMP && currentPage === 1 ? getKeywordConfig(keyword) : null;

	const allSchemas = nmpKeywordConfig
		? []
		: [
				organizationSchema,
				breadcrumbListSchema,
				clpWebPageSchema,
				clpWebSiteSchema,
				...facilitySchemas
			];

	const CombinedSchemaComponent = combineSchemas(allSchemas);

	const nmpSchema = nmpKeywordConfig
		? buildNmpSchema({
				facilityCards,
				baseUrl,
				meta,
				headline: headingTitle
			})
		: null;

	// TODO: check if we can parse text to H2 in SEO-Service itself
	const updatedPageText = pageText
		? pageText
				.replace("Finding storage", "Finding Storage Units")
				.replaceAll("<h3>", "<h2>")
				.replace(/<\/h3>/g, "</h2>")
				.replaceAll("<h4>", "<h3>")
				.replace(/<\/h4>/g, "</h3>")
		: "";

	const selectedCardForOverlay = useMemo(
		() =>
			pinSelected && selectedFacilityId
				? (facilityCards.find((c) => c.id === selectedFacilityId) ??
					null)
				: null,
		[facilityCards, pinSelected, selectedFacilityId]
	);

	const onOverlayTransitionEnd = useCallback(
		(e) => {
			if (isOverlayClosing && e.propertyName === "transform") {
				setPinSelected(false);
				setSelectedFacilityId(null);
				setIsOverlayClosing(false);
			}
		},
		[
			isOverlayClosing,
			setPinSelected,
			setSelectedFacilityId,
			setIsOverlayClosing
		]
	);

	const showStickToolBar = totalListings == null || totalListings >= 0;

	const handleDealsFilterChange = (filterValue) => {
		dispatch(
			applySearchClient(
				pathname,
				{ ...queryParams, page: 1, dealsFilter: filterValue },
				navigate
			)
		);
	};

	return (
		<Page
			className={classnames("search-page-v2", {
				"search-page-v2--no-toolbar": !showStickToolBar
			})}
			id="searchPage"
			title={meta.title}
			pageType={pageType}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={meta.noindex}
			noFollow={meta.nofollow}
			isCLP={isCLP}
			isSearchPage
		>
			<SearchPageContext.Provider value={searchPageContext}>
				{CombinedSchemaComponent}
				{nmpSchema && (
					<Helmet>
						<script type="application/ld+json">
							{JSON.stringify(nmpSchema)}
						</script>
					</Helmet>
				)}
				{showStickToolBar && <StickToolBar {...stickToolBarProps} />}
				<div>
					<div className="full-page-scrollable">
						<div ref={mapAndListRef}>
							<Row
								className="page-layout"
								gutter="xlarge"
							>
								<SearchPageV2MapColumn
									showMapView={showMapView}
									mapCenter={mapCenter}
									facilityCards={facilityCards}
									handleMapMove={handleMapMove}
									handleMapLocationClick={
										handleMapLocationClick
									}
									showMapLoader={showMapLoader}
									mapMoveMessage={mapMoveMessage}
									searchFromMove={searchFromMove}
									setSearchFromMove={setSearchFromMove}
									selectedFacilityId={selectedFacilityId}
									hoveredFacilityId={hoveredFacilityId}
									setHoveredFacilityId={setHoveredFacilityId}
									mapRef={mapRef}
									googleMapsRef={googleMapsRef}
									selectedCardForOverlay={
										selectedCardForOverlay
									}
									isOverlayClosing={isOverlayClosing}
									onOverlayTransitionEnd={
										onOverlayTransitionEnd
									}
									locationName={
										preferredPlaceName && state
											? `${preferredPlaceName}, ${state}`
											: preferredPlaceName
									}
								/>

								<Column
									className={`main ${
										showMapView
											? "hide-list-view"
											: "show-list-view"
									}`}
									small={12}
									medium={5}
									mediumLarge={6}
									gutter="xlarge"
								>
									<div className="main-scroll-inner">
										{isDealsPage && (
											<div className="deals-filter-row">
												<DealsFilter
													value={dealsFilter}
													onChange={
														handleDealsFilterChange
													}
												/>
											</div>
										)}
										<SearchResults
											facilityCards={facilityCards}
											currentPage={currentPage}
											selectedFacilityId={
												selectedFacilityId
											}
											hoveredFacilityId={
												hoveredFacilityId
											}
											onFacilityHover={
												setHoveredFacilityId
											}
											renderFacilitySchema={!isCLP}
											subPageType={subPageType}
										/>

										{hasMorePages && (
											<Button
												theme={
													isLoadingResults
														? "tertiary"
														: "secondary"
												}
												onClick={loadMore}
												className="load-more-button"
											>
												{isLoadingResults ? (
													<LoadingBoxes
														size="huge"
														primaryColor="white"
														secondaryColor="gray"
													/>
												) : (
													`Load ${
														currentFacilityCount >
														totalListings - 20
															? totalListings -
																currentFacilityCount
															: 20
													} More`
												)}
											</Button>
										)}
									</div>
								</Column>
							</Row>
						</div>
					</div>

					<Container className="search-page-content">
						<SearchBar
							buttonText="Search"
							id="bottom-search-bar"
							label="Enter ZIP code..."
							theme="lightBlue"
						>
							<Text
								size="large"
								weight="bold"
							>
								Update Your Storage Search
							</Text>
							<Paragraph
								last
								size="small"
							>
								Find storage units near you
							</Paragraph>
						</SearchBar>

						{updatedPageText && (
							<HTMLContent
								className="seoContent"
								content={updatedPageText}
							/>
						)}

						{showSearchLandingContent && (
							<Section className="search-landing-content">
								{amenityPages && (
									<SisterPages
										header={`Browse ${getDisplayCityName(preferredPlaceName)} Storage Units by Amenity`}
										cityName={getDisplayCityName(
											preferredPlaceName
										)}
										links={amenityPages}
									/>
								)}
								{nearbyCities?.length > 0 && (
									<NearbyCities
										nearbyCities={nearbyCities}
										location={location}
									/>
								)}
							</Section>
						)}
						<Breadcrumbs
							location={location}
							cityUrl={isNMP ? "" : cityUrl}
							stateUrl={isNMP ? "" : stateUrl}
							isCLP={isCLP}
							canonicalUrl={meta?.canonical}
							isSearchPage
							breadcrumbList={isNMP ? breadcrumbItems : undefined}
						/>
					</Container>
				</div>

				<ClickToCallTray
					isMapView={showMapView}
					onToggleMapView={handleShowMapView}
				/>

				{((showMapLoader && searchFromMove && isSearchLoading) ||
					isFiltering) && (
					<div
						className="search-page-map-loader-overlay"
						aria-hidden="false"
						aria-busy="true"
						aria-label="Loading new results"
					>
						<LoadingBoxes
							size="huge"
							primaryColor="blue"
							secondaryColor="gray"
						/>
					</div>
				)}
			</SearchPageContext.Provider>
			<ScrollToTop />
		</Page>
	);
}
